import MuxPlayer, { MuxPlayerRefAttributes } from '@mux/mux-player-react';
import { useLocalSearchParams } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { CHROMECAST_APPLICATION_ID } from '@/constants/config';
import { decryptObject } from '@/utils/crypto';

const MuxPlayerScreen = () => {
  const { playbackId, payload } = useLocalSearchParams<{
    playbackId: string;
    payload: string;
  }>();

  const [payloadState, setPayloadState] = useState<{
    playbackId?: string;
    token?: string;
    drmToken?: string;
    lastTimeStamp?: number;
  }>();
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const loadPayloadParams = async () => {
      const payloadObject = await decryptObject<{
        playbackId?: string;
        token?: string;
        drmToken?: string;
        lastTimeStamp?: number;
      }>(decodeURIComponent(payload));

      if (payloadObject) {
        setPayloadState(payloadObject);
      }
    };

    if (payload) {
      loadPayloadParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const muxRef = useRef<MuxPlayerRefAttributes>(null);
  const sourceMuxRef = useRef<MuxPlayerRefAttributes>(null);
  const lastSentRef = useRef<number>(0);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'pause') {
        muxRef.current?.pause?.();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const player = sourceMuxRef.current;
    if (player && currentTime > 0) {
      player.currentTime = currentTime;
      player.play();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadState?.playbackId]);

  const onEnded = () => {
    const player = sourceMuxRef.current;
    if (!player) return;

    const typeSafeWindow = window as any;
    if (typeSafeWindow.ReactNativeWebView && typeSafeWindow.ReactNativeWebView.postMessage) {
      typeSafeWindow.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'episode-ended'
        })
      );
    }
  };

  const onTimeUpdate = () => {
    const player = sourceMuxRef.current;
    if (!player) return;
    const now = Date.now();

    if (now - lastSentRef.current >= 10000) {
      lastSentRef.current = now;

      const typeSafeWindow = window as any;
      if (typeSafeWindow.ReactNativeWebView && typeSafeWindow.ReactNativeWebView.postMessage) {
        typeSafeWindow.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: 'save-timestamp',
            currentTime: player.currentTime
          })
        );
      }
    }
  };

  if (!playbackId && !payloadState?.playbackId) {
    return <p>Ups! An error has ocured</p>;
  }

  if (payloadState?.playbackId) {
    return (
      <View style={styles.mainContainer}>
        <MuxPlayer
          ref={sourceMuxRef}
          style={{
            contain: 'strict',
            ...styles.playerStyle
          }}
          playbackId={payloadState.playbackId}
          tokens={{
            playback: payloadState.token,
            drm: payloadState.drmToken
          }}
          castReceiver={CHROMECAST_APPLICATION_ID}
          autoPlay
          startTime={payloadState.lastTimeStamp}
          onEnded={onEnded}
          onTimeUpdate={onTimeUpdate}
          onError={(event) => {
            const error = event as any;
            const muxError = error?.detail;

            if (muxError?.data?.response?.code === 403 || muxError?.code === 3) {
              console.warn('MUX token may have expired. Fetching new one...');

              const time = sourceMuxRef.current?.currentTime || 0;
              setCurrentTime(time);

              const typeSafeWindow = window as any;
              if (typeSafeWindow.ReactNativeWebView && typeSafeWindow.ReactNativeWebView.postMessage) {
                typeSafeWindow.ReactNativeWebView.postMessage(JSON.stringify({ type: 'mux-token-expired' }));
              } else {
                console.warn('Not in React Native WebView');
                console.error('MUX Player error', error);
              }
            } else {
              console.error('MUX Player error', error);
            }
          }}
        />
      </View>
    );
  }

  return (
    <View style={styles.mainContainer}>
      <MuxPlayer
        ref={muxRef}
        style={{
          contain: 'strict',
          ...styles.playerStyle
        }}
        playbackId={playbackId}
        autoPlay
      />
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    backgroundColor: 'black',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%'
  },

  playerStyle: {
    backgroundColor: 'black',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%'
  }
});

export default MuxPlayerScreen;
